import { useEffect, useState } from "react";
import { api, fmtError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

function localDate(value) {
  if (!value) return "";
  const date = new Date(value);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

export default function ExamEntryControls({ session, onChange }) {
  const [deadline, setDeadline] = useState(localDate(session.entry_deadline_at));
  const [busy, setBusy] = useState(false);
  const [queue, setQueue] = useState(null);
  const [confirmEntryChange, setConfirmEntryChange] = useState(false);
  useEffect(() => setDeadline(localDate(session.entry_deadline_at)), [session.id, session.entry_deadline_at]);
  useEffect(() => {
    let stopped = false, timer;
    const refresh = async () => {
      try {
        const response = await api.get(`/live-sessions/${session.id}/entry-queue`);
        if (!stopped) setQueue(response.data);
      } catch (_) { if (!stopped) setQueue(null); }
      finally { if (!stopped) timer = setTimeout(refresh, 5000); }
    };
    if (session.phase !== "finished") refresh();
    return () => { stopped = true; clearTimeout(timer); };
  }, [session.id, session.phase]);
  if (session.phase === "finished") return null;
  const closed = session.entry_open === false || (session.entry_deadline_at && Date.now() >= new Date(session.entry_deadline_at).getTime());
  const save = async (opened, value) => {
    setBusy(true);
    try {
      const response = await api.post(`/live-sessions/${session.id}/entry-policy`, {
        entry_open: opened, entry_deadline_at: value ? new Date(value).toISOString() : null,
      });
      onChange(response.data);
      setConfirmEntryChange(false);
      toast.success(opened ? "Política de entrada atualizada." : "Entrada fechada. Quem já está na fila mantém a vez.");
    } catch (error) { toast.error(fmtError(error)); }
    finally { setBusy(false); }
  };
  return <section data-testid="exam-entry-controls" className="border border-border bg-card p-5 space-y-4">
    <div className="flex flex-wrap justify-between items-center gap-4">
      <div><div className="label-eyebrow">entrada e fila de espera</div>
        <h2 className="font-bold mt-1">{closed ? "Fechada para novas pessoas" : "Aberta para novas pessoas"}</h2>
        {queue?.enabled && <p className="text-sm text-muted-foreground mt-1">{queue.waiting + queue.offered} pessoa(s) aguardando nesta prova{queue.mode === "paused" ? " · liberação temporariamente pausada por segurança" : ""}</p>}
      </div>
      <Button variant={closed ? "default" : "outline"} disabled={busy} className="rounded-none" onClick={() => setConfirmEntryChange(true)}>{closed ? "Reabrir entrada" : "Fechar entrada na fila"}</Button>
    </div>
    {confirmEntryChange && <div role="alert" className="border border-amber-500 bg-amber-50 p-4 space-y-3">
      <p className="text-sm text-amber-950">{closed ? "Reabrir a entrada sem horário-limite?" : "Fechar a entrada para novas pessoas? Quem já está na fila desta prova ou já começou continuará."}</p>
      <div className="flex gap-3"><Button disabled={busy} onClick={() => save(Boolean(closed), closed ? "" : session.entry_deadline_at)}>{closed ? "Confirmar reabertura" : "Confirmar fechamento"}</Button>
        <Button variant="outline" disabled={busy} onClick={() => setConfirmEntryChange(false)}>Cancelar</Button></div>
    </div>}
    <div className="flex flex-wrap gap-3 items-end">
      <label className="flex-1 min-w-56"><span className="label-eyebrow">horário-limite de entrada (opcional, horário local)</span>
        <Input aria-label="Horário-limite de entrada" type="datetime-local" value={deadline} onInput={(e) => setDeadline(e.currentTarget.value)} onChange={(e) => setDeadline(e.target.value)} disabled={busy} className="mt-2 rounded-none" />
      </label>
      <Button variant="outline" disabled={busy} onClick={() => save(session.entry_open !== false, deadline)} className="rounded-none">Salvar horário</Button>
    </div>
    <p className="text-sm text-muted-foreground">Fechar a entrada não encerra a prova nem remove quem já está na fila. O tempo individual só começa ao iniciar a tentativa, depois da admissão.</p>
  </section>;
}
